(() => {
  const EVENT_PREFIX = 'joylink:';
  const ATTRIBUTION_KEY = 'joylink_attribution';
  const SESSION_KEY = 'joylink_session_id';
  const SCROLL_MARKS = [25, 50, 75, 100];
  const DWELL_MARKS = [10, 30, 60, 120];
  const SECTION_DWELL_MARKS = [5, 10, 20, 30, 60, 120];
  const firedScrollMarks = new Set();
  const firedDwellMarks = new Set();
  const firedVideoMarks = new WeakMap();
  const META_EVENT_MAP = {
    'joylink:page_view': 'JoylinkPageView',
    'joylink:product_view': 'JoylinkProductView',
    'joylink:catalog_product_click': 'JoylinkCatalogProductClick',
    'joylink:product_card_click': 'JoylinkProductCardClick',
    'joylink:add_to_cart_intent': 'JoylinkAddToCartIntent',
    'joylink:checkout_button_click': 'JoylinkCheckoutButtonClick',
    'joylink:variant_selected': 'JoylinkVariantSelected',
    'joylink:variant_changed': 'JoylinkVariantChanged',
    'joylink:product_media_click': 'JoylinkProductMediaClick',
    'joylink:localization_interaction': 'JoylinkLocalizationInteraction',
    'joylink:scroll_depth': 'JoylinkScrollDepth',
    'joylink:dwell_time': 'JoylinkDwellTime',
    'joylink:section_dwell_time': 'JoylinkSectionDwellTime',
    'joylink:section_dwell_summary': 'JoylinkSectionDwellSummary',
    'joylink:video_play': 'JoylinkVideoPlay',
    'joylink:video_progress': 'JoylinkVideoProgress',
    'joylink:site_search': 'JoylinkSiteSearch',
  };

  const getSessionId = () => {
    try {
      const existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) return existing;
      const next = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, next);
      return next;
    } catch {
      return '';
    }
  };

  const getTemplate = () => document.querySelector('main[data-template]')?.dataset.template || '';

  const getPageContext = () => ({
    page_title: document.title,
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_template: getTemplate(),
    language: document.documentElement.lang || '',
    referrer: document.referrer || '',
    session_id: getSessionId(),
  });

  const readAttribution = () => {
    try {
      return JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || '{}');
    } catch {
      return {};
    }
  };

  const persistAttribution = () => {
    const params = new URLSearchParams(window.location.search);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'ttclid', 'msclkid'];
    const captured = {};

    keys.forEach((key) => {
      const value = params.get(key);
      if (value) captured[key] = value;
    });

    if (Object.keys(captured).length === 0) return readAttribution();

    const attribution = {
      ...readAttribution(),
      ...captured,
      landing_page: window.location.href,
      captured_at: new Date().toISOString(),
    };

    try {
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    } catch {
      // Ignore storage restrictions.
    }

    return attribution;
  };

  const normalizeEventName = (name) => (name.startsWith(EVENT_PREFIX) ? name : `${EVENT_PREFIX}${name}`);

  const compactObject = (object) => Object.entries(object).reduce((memo, [key, value]) => {
    if (value === undefined || value === null || value === '') return memo;
    memo[key] = value;
    return memo;
  }, {});

  let cartSnapshot = null;
  let cartSnapshotPromise = null;

  const getCartUrl = () => {
    const cartUrl = window.routes?.cart_url || '/cart';
    return `${cartUrl.replace(/\/$/, '')}.js`;
  };

  const normalizeCartValue = (cart) => {
    const cents = Number(cart?.total_price);
    if (!Number.isFinite(cents)) return undefined;
    return Math.round(cents) / 100;
  };

  const getPageCurrency = () => (
    cartSnapshot?.currency
    || window.Shopify?.currency?.active
    || window.ShopifyAnalytics?.meta?.currency
    || ''
  );

  const getCheckoutContext = () => {
    const value = normalizeCartValue(cartSnapshot);
    return compactObject({
      currency: getPageCurrency(),
      value,
      price: value,
      cart_token: cartSnapshot?.token,
      cart_item_count: cartSnapshot?.item_count,
    });
  };

  const hydrateCheckoutButtons = () => {
    const checkoutContext = getCheckoutContext();
    if (!checkoutContext.currency && checkoutContext.value === undefined) return;

    document.querySelectorAll('[name="checkout"], [href*="/checkout"], .cart__checkout-button').forEach((button) => {
      if (checkoutContext.currency) button.dataset.currency = checkoutContext.currency;
      if (checkoutContext.value !== undefined) button.dataset.value = String(checkoutContext.value);
    });
  };

  const setCartSnapshot = (cart) => {
    if (!cart || typeof cart !== 'object') return;
    cartSnapshot = cart;
    hydrateCheckoutButtons();
  };

  const fetchCartSnapshot = (force = false) => {
    if (cartSnapshotPromise && !force) return cartSnapshotPromise;

    cartSnapshotPromise = fetch(getCartUrl(), { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        setCartSnapshot(cart);
        return cartSnapshot;
      })
      .catch(() => cartSnapshot)
      .finally(() => {
        cartSnapshotPromise = null;
      });

    return cartSnapshotPromise;
  };

  const publishMetaEvent = (eventName, data) => {
    if (typeof window.fbq !== 'function') return;

    const metaEventName = META_EVENT_MAP[eventName];
    if (!metaEventName) return;

    const attribution = data.attribution || {};
    const valueSource = data.price !== undefined && data.price !== '' ? data.price : data.value;
    const value = Number.parseFloat(valueSource);
    const contentId = data.variant_id || data.product_id || '';
    const params = compactObject({
      page_title: data.page_title,
      page_url: data.page_url,
      page_path: data.page_path,
      page_template: data.page_template,
      session_id: data.session_id,
      content_name: data.product_title || data.link_text || data.page_title,
      content_type: data.product_id ? 'product' : undefined,
      content_ids: contentId ? [String(contentId)] : undefined,
      product_id: data.product_id,
      product_handle: data.product_handle,
      variant_id: data.variant_id,
      variant_title: data.variant_title,
      option_name: data.option_name,
      option_value: data.option_value,
      currency: data.currency,
      value: Number.isNaN(value) ? undefined : value,
      quantity: data.quantity,
      depth_percent: data.depth_percent,
      seconds: data.seconds,
      section_name: data.section_name,
      section_label: data.section_label,
      active_seconds: data.active_seconds,
      total_visible_seconds: data.total_visible_seconds,
      progress_percent: data.progress_percent,
      search_string: data.search_term,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_content: attribution.utm_content,
      utm_term: attribution.utm_term,
      fbclid: attribution.fbclid,
    });

    window.fbq('trackCustom', metaEventName, params);
  };

  const publishEvent = (name, payload = {}) => {
    const eventName = normalizeEventName(name);
    const data = {
      ...getPageContext(),
      attribution: readAttribution(),
      ...payload,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...data,
    });

    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, data);
    }

    publishMetaEvent(eventName, data);

    window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
  };

  const closestProductLink = (target) => {
    const link = target.closest('a[href*="/products/"]');
    if (!link) return null;

    const card = link.closest('.product-card-wrapper, product-component, .card-wrapper');
    if (!card && !link.closest('main')) return null;

    return link;
  };

  const getProductHandleFromUrl = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch {
      return '';
    }
  };

  const getCurrentProductContext = () => {
    const variantPicker = document.querySelector('variant-selects[data-product-id]');
    const productForm = document.querySelector('product-form form[action*="/cart/add"], form[action*="/cart/add"]');
    const selectedVariant = (() => {
      try {
        return JSON.parse(variantPicker?.querySelector('[data-selected-variant]')?.textContent || '{}');
      } catch {
        return {};
      }
    })();

    return {
      product_id: variantPicker?.dataset.productId || '',
      product_handle: variantPicker?.dataset.productHandle || getProductHandleFromUrl(window.location.href),
      product_title: variantPicker?.dataset.productTitle || document.querySelector('h1')?.textContent?.trim() || '',
      variant_id: selectedVariant.id || productForm?.querySelector('[name="id"]')?.value || '',
      variant_title: selectedVariant.title || '',
      currency: variantPicker?.dataset.currencyCode || window.Shopify?.currency?.active || '',
      price: variantPicker?.dataset.selectedPriceAmount || '',
    };
  };

  const trackPageViews = () => {
    publishEvent('page_view');

    if (getTemplate() === 'product') {
      publishEvent('product_view', getCurrentProductContext());
    }
  };

  const trackClicks = () => {
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const productLink = closestProductLink(target);
      if (productLink) {
        publishEvent(getTemplate() === 'collection' ? 'catalog_product_click' : 'product_card_click', {
          link_url: productLink.href,
          product_handle: getProductHandleFromUrl(productLink.href),
          link_text: productLink.textContent?.trim().slice(0, 120) || '',
        });
      }

      const checkoutButton = target.closest('[name="checkout"], [href*="/checkout"], .cart__checkout-button');
      if (checkoutButton) {
        publishEvent('checkout_button_click', {
          ...getCheckoutContext(),
          button_text: checkoutButton.textContent?.trim().slice(0, 120) || '',
        });
      }

      const addToCartButton = target.closest('product-form [type="submit"], form[action*="/cart/add"] [type="submit"]');
      if (addToCartButton) {
        const form = addToCartButton.closest('form');
        const formData = form ? new FormData(form) : null;
        publishEvent('add_to_cart_intent', {
          variant_id: formData?.get('id') || '',
          quantity: Number(formData?.get('quantity') || 1),
          button_text: addToCartButton.textContent?.trim().slice(0, 120) || '',
        });
      }

      const mediaButton = target.closest('[data-media-id], .product__media-item, .thumbnail, .product__media-toggle');
      if (mediaButton) {
        publishEvent('product_media_click', {
          media_id: mediaButton.dataset.mediaId || mediaButton.closest('[data-media-id]')?.dataset.mediaId || '',
        });
      }

      const localizationControl = target.closest('localization-form button, localization-form a, [name="locale_code"], [name="country_code"]');
      if (localizationControl) {
        publishEvent('localization_interaction', {
          control_text: localizationControl.textContent?.trim().slice(0, 120) || '',
          control_name: localizationControl.getAttribute('name') || '',
        });
      }
    });
  };

  const trackVariantChanges = () => {
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (!target.closest('variant-selects')) return;

      const wrapper = target.closest('variant-selects');
      const selectedVariant = (() => {
        try {
          return JSON.parse(wrapper.querySelector('[data-selected-variant]')?.textContent || '{}');
        } catch {
          return {};
        }
      })();

      publishEvent('variant_selected', {
        product_id: wrapper.dataset.productId || '',
        product_handle: wrapper.dataset.productHandle || '',
        product_title: wrapper.dataset.productTitle || '',
        variant_id: selectedVariant.id || target.value || '',
        variant_title: selectedVariant.title || '',
        option_name: target.name || '',
        option_value: target.value || target.getAttribute('value') || '',
      });
    });

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      subscribe(window.PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
        publishEvent('variant_changed', {
          variant_id: data?.variant?.id || '',
          variant_title: data?.variant?.title || '',
        });
      });
    }
  };

  const trackScrollDepth = () => {
    const measure = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;

      const percent = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      SCROLL_MARKS.forEach((mark) => {
        if (percent >= mark && !firedScrollMarks.has(mark)) {
          firedScrollMarks.add(mark);
          publishEvent('scroll_depth', { depth_percent: mark });
        }
      });
    };

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        measure();
      });
    }, { passive: true });

    measure();
  };

  const trackDwellTime = () => {
    DWELL_MARKS.forEach((seconds) => {
      window.setTimeout(() => {
        if (document.visibilityState !== 'visible' || firedDwellMarks.has(seconds)) return;
        firedDwellMarks.add(seconds);
        publishEvent('dwell_time', { seconds });
      }, seconds * 1000);
    });
  };

  const queryFirstTrackable = (selectors) => {
    for (const selector of selectors) {
      let elements = [];
      try {
        elements = Array.from(document.querySelectorAll(selector));
      } catch {
        elements = [];
      }

      const element = elements.find((candidate) => {
        const rect = candidate.getBoundingClientRect();
        return rect.width >= 120 && rect.height >= 60;
      });

      if (element) return element;
    }

    return null;
  };

  const getVisibilityRatio = (element) => {
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return 0;

    const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));

    return (visibleWidth * visibleHeight) / (rect.width * rect.height);
  };

  const getProductSectionTargets = () => [
    {
      section_name: 'product_details',
      section_label: 'Product details area',
      element: queryFirstTrackable([
        '.product__description',
        '.product__accordion',
        '.collapsible-content',
        '.shopify-section[id*="story"]',
        '.shopify-section[id*="detail"]',
        'section[class*="story"]',
        'section[class*="detail"]',
      ]),
    },
    {
      section_name: 'product_reviews',
      section_label: 'Reviews area',
      element: queryFirstTrackable([
        '#judgeme_product_reviews',
        '.jdgm-rev-widg',
        '.jdgm-widget',
        '.spr-container',
        '.shopify-product-reviews',
        '.shopify-section[id*="review"]',
        'section[id*="review"]',
        'section[class*="review"]',
        'div[id*="review"]',
      ]),
    },
    {
      section_name: 'add_to_cart',
      section_label: 'Add to cart area',
      element: queryFirstTrackable([
        'product-form',
        '.product-form__buttons',
        '.product-form',
        '[id^="ProductSubmitButton-"]',
        '.shopify-payment-button',
      ]),
    },
  ].filter(({ element }) => element);

  const trackProductSectionDwell = () => {
    if (getTemplate() !== 'product') return;

    const sections = getProductSectionTargets().map((section) => ({
      ...section,
      seconds: 0,
      firedMarks: new Set(),
    }));

    if (sections.length === 0) return;

    const fireMark = (section, mark) => {
      if (section.firedMarks.has(mark)) return;

      section.firedMarks.add(mark);
      publishEvent('section_dwell_time', {
        ...getCurrentProductContext(),
        section_name: section.section_name,
        section_label: section.section_label,
        active_seconds: mark,
        total_visible_seconds: section.seconds,
      });
    };

    const tick = () => {
      if (document.visibilityState !== 'visible') return;

      sections.forEach((section) => {
        if (!document.documentElement.contains(section.element)) return;
        if (getVisibilityRatio(section.element) < 0.35) return;

        section.seconds += 1;
        SECTION_DWELL_MARKS.forEach((mark) => {
          if (section.seconds >= mark) fireMark(section, mark);
        });
      });
    };

    const intervalId = window.setInterval(tick, 1000);

    window.addEventListener('pagehide', () => {
      window.clearInterval(intervalId);
      sections.forEach((section) => {
        if (section.seconds <= 0) return;

        publishEvent('section_dwell_summary', {
          ...getCurrentProductContext(),
          section_name: section.section_name,
          section_label: section.section_label,
          total_visible_seconds: section.seconds,
        });
      });
    }, { once: true });
  };

  const trackVideos = () => {
    document.addEventListener('play', (event) => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement)) return;
      publishEvent('video_play', {
        video_src: video.currentSrc || video.src || '',
        video_title: video.closest('[class*="video"]')?.querySelector('p, h2, h3')?.textContent?.trim().slice(0, 120) || '',
      });
    }, true);

    document.addEventListener('timeupdate', (event) => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement) || !video.duration) return;

      const percent = Math.round((video.currentTime / video.duration) * 100);
      const marks = firedVideoMarks.get(video) || new Set();
      [25, 50, 75, 100].forEach((mark) => {
        if (percent >= mark && !marks.has(mark)) {
          marks.add(mark);
          publishEvent('video_progress', {
            progress_percent: mark,
            video_src: video.currentSrc || video.src || '',
          });
        }
      });
      firedVideoMarks.set(video, marks);
    }, true);
  };

  const trackSearch = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[action*="/search"], form[role="search"]')) return;

      const formData = new FormData(form);
      publishEvent('site_search', {
        search_term: formData.get('q') || '',
      });
    });
  };

  const trackCartSnapshot = () => {
    fetchCartSnapshot();

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
        if (cartData) {
          setCartSnapshot(cartData);
          return;
        }

        fetchCartSnapshot(true);
      });
    }
  };

  const init = () => {
    persistAttribution();
    trackCartSnapshot();
    trackPageViews();
    trackClicks();
    trackVariantChanges();
    trackScrollDepth();
    trackDwellTime();
    trackProductSectionDwell();
    trackVideos();
    trackSearch();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
